import { useState, useEffect, useRef, useCallback } from 'react';
import { DatasetGenerator } from '@/core/DatasetGenerator';
import { MetricsCollectorImpl } from '@/core/MetricsCollectorImpl';
import { computeState } from '@/core/computeState';
import { Algorithms } from '@/algorithms';
import type { SortItem, SortEvent, SortAlgorithm } from '@/core/types';
import { useSortStore } from './store';
import { ControlPanel } from './components/ControlPanel';
import { TimelineScrubber } from './components/TimelineScrubber';
import { BarRenderer } from './renderers/BarRenderer';
import { MetricsPanel } from './components/MetricsPanel';
import { ExplanationPanel } from './components/ExplanationPanel';
import { CodePanel } from './components/CodePanel';
import { IntroPanel } from './components/IntroPanel';
import { HelpPanel } from './components/HelpPanel';
import { Colors } from '@/styles/colors';

const s: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: Colors.light.background,
    color: Colors.light.text,
    minHeight: '100vh',
    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    padding: 'clamp(16px, 3vw, 24px) clamp(16px, 4vw, 32px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    borderBottom: `1px solid ${Colors.light.border}`,
    backgroundColor: Colors.light.surface,
  },
  headerLeft: { display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' },
  logo: { fontSize: 24, fontWeight: 700, color: Colors.light.primary, margin: 0, letterSpacing: 0 },
  tagline: { fontSize: 13, color: Colors.light.textSecondary, margin: '2px 0 0' },
  statusBadge: {
    padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600,
    backgroundColor: Colors.light.background, border: `1px solid ${Colors.light.border}`, color: Colors.light.textSecondary,
  },
  main: {
    width: '100%', maxWidth: 1440, margin: '0 auto', padding: 'clamp(16px, 4vw, 32px)',
    display: 'flex', flexDirection: 'column', gap: 24,
  },
  vizCard: {
    backgroundColor: Colors.light.surface, borderRadius: 8, overflow: 'hidden',
    border: `1px solid ${Colors.light.border}`,
  },
  stage: { width: '100%', height: 'clamp(300px, 46vw, 420px)', position: 'relative', display: 'block' },
  legendRow: {
    display: 'flex', gap: 16, padding: '10px 20px',
    borderTop: `1px solid ${Colors.light.border}`, flexWrap: 'wrap',
    backgroundColor: Colors.light.background,
  },
  legendChip: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: Colors.light.textSecondary },
  legendDot: { width: 10, height: 10, borderRadius: 3 },
  panelsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    gap: 24,
  },
  panel: { minWidth: 0 },
};

const LEGEND_ITEMS = [
  { label: 'Diam', color: Colors.status.idle },
  { label: 'Dibandingkan', color: Colors.status.comparing },
  { label: 'Ditukar', color: Colors.status.swapping },
  { label: 'Dipilih', color: Colors.status.selected },
  { label: 'Pivot', color: Colors.status.pivot },
  { label: 'Selesai', color: Colors.status.sorted },
];

export default function App() {
  const [sortItems, setSortItems] = useState<SortItem[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [algo, setAlgo] = useState<SortAlgorithm>(Algorithms[0]);
  const [dataset, setDataset] = useState('random');
  const [speed, setSpeed] = useState(1);
  const stageRef = useRef<HTMLDivElement>(null);

  const { events, setEvents, metrics, setMetrics, isPlaying, setIsPlaying, setCurrentEventIndex } = useSortStore();
  const eventsRef = useRef<SortEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- compute item state at current event index ---
  const stateItems = computeState(sortItems, events, currentIdx);

  // --- generate dataset ---
  useEffect(() => {
    const preset = DatasetGenerator.getPresetConfig(dataset);
    setSortItems(preset.generator(preset.count));
    setCurrentIdx(0);
    setIsPlaying(false);
    eventsRef.current = [];
    setEvents([]);
  }, [dataset, setEvents, setIsPlaying]);

  // --- run algorithm ---
  useEffect(() => {
    if (sortItems.length === 0) return;
    const result = algo.run(sortItems);
    eventsRef.current = result;
    setEvents(result);
    setMetrics(new MetricsCollectorImpl().collect(result));
    setCurrentIdx(0);
    setCurrentEventIndex(0);
    setIsPlaying(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algo, sortItems]);

  // --- playback loop ---
  useEffect(() => {
    const step = () => {
      if (!isPlaying) return;
      if (currentIdx >= events.length) {
        setIsPlaying(false);
        return;
      }
      setCurrentIdx(prev => prev + 1);
      setCurrentEventIndex(currentIdx + 1);
      const delay = Math.max(8, 160 / speed);
      timerRef.current = setTimeout(step, delay);
    };
    if (isPlaying) timerRef.current = setTimeout(step, 0);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [isPlaying, currentIdx, events.length, speed, setCurrentEventIndex, setIsPlaying]);

  const playPause = useCallback(() => setIsPlaying(v => !v), [setIsPlaying]);
  const stepForward = useCallback(() => {
    setCurrentIdx(prev => Math.min(prev + 1, events.length));
    setCurrentEventIndex(Math.min(currentIdx + 1, events.length));
  }, [events.length, currentIdx, setCurrentEventIndex]);
  const stepBackward = useCallback(() => {
    setCurrentIdx(prev => Math.max(prev - 1, 0));
    setCurrentEventIndex(Math.max(currentIdx - 1, 0));
  }, [currentIdx, setCurrentEventIndex]);
  const reset = useCallback(() => {
    setIsPlaying(false);
    setCurrentIdx(0);
    setCurrentEventIndex(0);
  }, [setIsPlaying, setCurrentEventIndex]);
  const onSeek = useCallback((pos: number) => {
    setCurrentIdx(pos);
    setCurrentEventIndex(pos);
  }, [setCurrentEventIndex]);

  const presetInfo = DatasetGenerator.getPresetConfig(dataset);

  return (
    <div style={s.container}>
      {/* ---- header ---- */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <h1 style={s.logo}>SortLab</h1>
          <span style={s.tagline}>Visualizer algoritma sorting interaktif</span>
        </div>
        <div style={s.statusBadge}>
          {algo.name} - {presetInfo.count} data
        </div>
      </header>

      <main style={s.main}>
        <IntroPanel
          algorithmName={algo.name}
          dataset={dataset}
          datasetDescription={presetInfo.description}
          itemCount={presetInfo.count}
        />

        {/* ---- visualization card ---- */}
        <div style={s.vizCard}>
          <div ref={stageRef} style={s.stage}>
            <BarRenderer stateItems={stateItems} stageRef={stageRef} />
          </div>
          {/* legend */}
          <div style={s.legendRow}>
            {LEGEND_ITEMS.map(l => (
              <div key={l.label} style={s.legendChip}>
                <div style={{ ...s.legendDot, backgroundColor: l.color }} />
                {l.label}
              </div>
            ))}
            <div style={{ flex: 1 }} />
            <div style={{ ...s.legendChip, color: Colors.light.textSecondary }}>
              Langkah: {currentIdx}/{events.length}
            </div>
          </div>
          <TimelineScrubber events={events} currentEventIndex={currentIdx} isPlaying={isPlaying} onSeek={onSeek} />
        </div>

        {/* ---- controls + metrics ---- */}
        <div style={s.panelsRow}>
          <div style={s.panel}>
            <ControlPanel
              algorithm={algo} dataset={dataset} speed={speed} isPlaying={isPlaying}
              onAlgorithmChange={(n) => { const a = Algorithms.find(x => x.name === n); if (a) { setAlgo(a); reset(); } }}
              onDatasetChange={setDataset}
              onSpeedChange={setSpeed}
              onPlayPause={playPause}
              onStepForward={stepForward}
              onStepBackward={stepBackward}
              onReset={reset}
            />
          </div>
          <div style={s.panel}>
            <MetricsPanel metrics={metrics} algorithm={algo} />
          </div>
        </div>

        {/* ---- explanation + code ---- */}
        <div style={s.panelsRow}>
          <div style={s.panel}>
            <ExplanationPanel algorithm={algo} />
          </div>
          <div style={s.panel}>
            <CodePanel algorithm={algo} />
          </div>
        </div>

        <HelpPanel />
      </main>
    </div>
  );
}
